import {
  ColumnApiModule,
  ColumnsToolPanelModule,
  ColumnMenuModule,
  ExternalFilterModule,
  ModuleRegistry,
  RenderApiModule,
  SetFilterModule,
  ValidationModule,
  ColDef,
  ICellRendererParams,
} from 'ag-grid-enterprise';
import type {
  ColumnMovedEvent,
  ColumnPinnedEvent,
  ColumnResizedEvent,
  ColumnVisibleEvent,
  GridApi,
  GridReadyEvent,
  RowHeightParams,
} from 'ag-grid-community';
import { Dropdown } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { Chip, Typography } from 'Components/atoms';
import { ConfirmationModal } from 'Components/molecules';
import { EmptyState } from 'Components/molecules/EmptyState';
import { EngagementsModal } from 'Components/organisms/ActiveEngagementsModal';
import { TestEngagementModal } from 'Components/organisms/TestEngagementModal';
import ChipMultiSelect, { OptionType } from 'Components/molecules/ChipMultiSelect/ChipMultiSelect';
import {
  DEFAULT_ERROR_MSG,
  DELETE_ENGAGEMENT,
  ENGAGE_SUCCESS_MSG,
  ENGAGEMENT_MODAL_CONTENT,
  FLOW_HEADER_TEXTS,
  FLOW_SPEC_TARGET_FINDINGS,
  SELECT_USERS_PLACEHOLDER_TEXT,
  TEST_ENGAGEMENT,
  TRIGGER_ENGAGEMENT,
  YES_DELETE_IT,
} from 'Core-utils/constants/constants';
import { DEFAULT_WORKFLOW_ROUTE } from 'Core-utils/routes';
import { isPendingEngagementsFilterByFindingsEnabled } from 'Core-utils/featureFlags/pendingEngagementsFilterByFindings';
import { DateTimeFormatter, getToastBody } from 'Core-utils/helpers/helpers';
import {
  AMP_SETTINGS,
  CohortDto,
  FilterCriteria,
  FindingSpecDto,
  FlowSpecDto,
  FlowSpecStatusKind,
  KIND,
  Page,
} from '@ampsec/platform-client';
import { useApiObjectData } from 'Hooks/useApiObjectData';
import { useConnectorData } from 'Hooks/useConnectorsData';
import { useEngagementMenuOptions, EngagementMenuOption } from 'Hooks/useEngagementMenuOptions';
import { useI18n } from 'Hooks/useI18n';
import { useProviderData } from 'Hooks/useProviderData';
import { useSearchParamsState } from 'Hooks/useSearchParamsState';
import { useTenantSettings } from 'Hooks/useTenantSettings';
import { ToastsContext } from 'Rdx/contexts/ToastsContext';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  AgGridBase,
  useAgGridColumnStatePersistence,
  useFetchAllPages,
} from '../AgGridBase';
import { useEngagementFlowClientFilter } from './useEngagementFlowClientFilter';
import {
  formatTargetFindingsForSearch,
  getEngagementFlowRowHeightForFindingCount,
  getTargetFindingCount,
} from './targetFindingDisplay.util';
import { getFindingKindsFromFlowSpec } from './flowSpecFindingKinds.util';
import {
  buildTagSpecIdToNameLookup,
  formatTagFiltersPlainLanguage,
  parseTagBucketsFromTriggerFilter,
} from './flowSpecTriggerTags.util';
import TargetFindingsCellRenderer from './TargetFindingsCellRenderer';
import { MultiValue } from 'react-select';
import { TbDotsVertical } from '@react-icons/all-files/tb/TbDotsVertical';
import { RiArrowDownSLine } from '@react-icons/all-files/ri/RiArrowDownSLine';
import { isEqual, startCase } from 'lodash';
import moment from 'moment';
import {
  getEngagementFlow,
  getFindingSpecs,
  getGridData,
  getUserCohorts,
  testEngagement,
  updateEngagementFlow,
} from 'Apis/library';
import { EngagementModalVariant } from 'Components/pages/EngagementFlowPage/engagementFlowTable';
import EngageIcon from 'Assets/icons/engagement.svg';
import {
  AgGridEngagementFlowProps,
  COLUMN_STATE_STORAGE_KEYS,
  getDefaultEngagementFlowColumnState,
  ENGAGEMENT_FLOW_FINDING_LINE_HEIGHT,
  ENGAGEMENT_FLOW_ROW_HEIGHT,
} from './AgGridEngagementFlow.config';
import {
  mapEngagementFlowGridStateToUrl,
  mapEngagementFlowUrlToGridState,
} from './engagementFlowUrlSync.util';
import { tagSpecsToOptions } from 'Core-utils/helpers/tagSpecList.util';
import { useTagSpecsCatalog } from 'Hooks/useTagSpecsCatalog';
import './styles.scss';

ModuleRegistry.registerModules([
  ColumnApiModule,
  RenderApiModule,
  ValidationModule,
  ColumnsToolPanelModule,
  ColumnMenuModule,
  SetFilterModule,
  ExternalFilterModule,
]);

const FLOW_SPEC_EXCLUDE_PRESET_SECURITY_TIP = 'securityTipFlowSpecs';

const TruncatedTextCellRenderer = (params: ICellRendererParams) => {
  const value = params.value?.toString() || '-';
  return (
    <div className="text-truncate bodyFont d-block" title={value}>
      {value}
    </div>
  );
};

const AgGridEngagementFlow: React.FC<AgGridEngagementFlowProps> = ({ onDuplicate, tableBodyHeight }) => {
  const navigate = useNavigate();
  const { t, formatDisplayLabel } = useI18n();
  const { filter: urlFilter, isUrlLoading, setSearchParamsState } = useSearchParamsState();
  const { addToast } = useContext(ToastsContext);
  const { tenantSettings, settingsLoading } = useTenantSettings();
  const moreOptions = useEngagementMenuOptions();

  // Modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedId, setSelectedId] = useState('');
  const [showEngagementsModal, setShowEngagementsModal] = useState(false);
  const [modalVariant, setModalVariant] = useState<EngagementModalVariant>('all');
  const [showTestEngagementModal, setShowTestEngagementModal] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState<OptionType[]>([]);
  const [testFlowSpec, setTestFlowSpec] = useState<FlowSpecDto>({} as FlowSpecDto);
  const [searchText, setSearchText] = useState('');
  const pendingEngagementsTagFilterEnabled = isPendingEngagementsFilterByFindingsEnabled();
  const { tagSpecs: tagSpecCatalog } = useTagSpecsCatalog({
    includeGlobals: true,
    enabled: pendingEngagementsTagFilterEnabled,
  });
  const tagSpecs = useMemo(
    () => (pendingEngagementsTagFilterEnabled ? tagSpecsToOptions(tagSpecCatalog) : []),
    [pendingEngagementsTagFilterEnabled, tagSpecCatalog],
  );

  const showMicrotrainingEngagements =
    (tenantSettings?.[AMP_SETTINGS.tenant.SHOW_MICROTRAINING_ENGAGEMENTS.id]?.value as boolean | undefined) ??
    AMP_SETTINGS.tenant.SHOW_MICROTRAINING_ENGAGEMENTS.defaultValue;

  const flowSpecFilterCriteria = useMemo<FilterCriteria>(
    () => ({
      limit: 250,
      sort: {
        name: 'ASC',
      },
      status: [FlowSpecStatusKind.ACTIVE, FlowSpecStatusKind.PAUSED, FlowSpecStatusKind.GHOST],
      ...(showMicrotrainingEngagements ? {} : { excludePreset: FLOW_SPEC_EXCLUDE_PRESET_SECURITY_TIP }),
    }),
    [showMicrotrainingEngagements],
  );

  const fetchFlowSpecsPage = useCallback(
    (params: FilterCriteria & { offset: number; limit: number }) =>
      getGridData('flowSpecs', params, '_summary') as Promise<Page<FlowSpecDto>>,
    []
  );

  const {
    rows: rowData,
    isLoading: isGridLoading,
    refetch: refresh,
  } = useFetchAllPages<FlowSpecDto, FilterCriteria, Page<FlowSpecDto>>({
    fetchPage: fetchFlowSpecsPage,
    baseParams: flowSpecFilterCriteria,
    pageSize: 250,
    maxRows: 2000,
    enabled: !settingsLoading,
  });

  const gridApiRef = useRef<GridApi<FlowSpecDto> | null>(null);

  const { connectorProviderMap } = useProviderData();

  const { data: findingSpecs } = useApiObjectData<FindingSpecDto[], FindingSpecDto[]>(
    KIND.FINDING_SPECS,
    getFindingSpecs,
    (findingSpecItems) => (Array.isArray(findingSpecItems) ? findingSpecItems : []),
    { limit: 250 },
  );

  const findingSpecById = useMemo(
    () =>
      (Array.isArray(findingSpecs) ? findingSpecs : []).reduce<Record<string, FindingSpecDto>>((acc, findingSpec) => {
        acc[findingSpec.id] = findingSpec;
        return acc;
      }, {}),
    [findingSpecs],
  );

  const formatDisplayLabelRef = useRef(formatDisplayLabel);
  formatDisplayLabelRef.current = formatDisplayLabel;
  const connectorProviderMapRef = useRef(connectorProviderMap);
  connectorProviderMapRef.current = connectorProviderMap;
  const findingSpecByIdRef = useRef(findingSpecById);
  findingSpecByIdRef.current = findingSpecById;

  const searchFields = useMemo(
    () => [
      (row: FlowSpecDto) => row.name,
      (row: FlowSpecDto) => row.status,
      (row: FlowSpecDto) => row.filter?.cohorts?.map((cohort) => cohort?.displayValue).filter(Boolean).join(', '),
      (row: FlowSpecDto) =>
        formatTargetFindingsForSearch(row, {
          connectorProviderMap: connectorProviderMapRef.current,
          findingSpecById: findingSpecByIdRef.current,
          formatDisplayLabel: formatDisplayLabelRef.current,
        }),
    ],
    [],
  );

  const { handleSearchChange: handleGridSearchChange, isExternalFilterPresent, doesExternalFilterPass } =
    useEngagementFlowClientFilter<FlowSpecDto>({
      fields: searchFields,
      onFilterChanged: () => gridApiRef.current?.onFilterChanged(),
    });

  const isEngagementFlowUrlSyncLoading = isUrlLoading || settingsLoading || isGridLoading;

  const engagementFlowUrlSync = useMemo(
    () => ({
      enabled: true,
      adapter: {
        fromUrl: (params: Record<string, string | string[] | undefined>) =>
          mapEngagementFlowUrlToGridState(params),
        toUrl: (state: { filterModel: Record<string, unknown>; sortModel: Array<{ colId: string; sort: 'asc' | 'desc' }> }) =>
          mapEngagementFlowGridStateToUrl(state),
      },
      debounceMs: 200,
      replaceHistory: true,
      getUrlState: () => urlFilter,
      setUrlState: (next: Record<string, string | string[] | undefined>) => setSearchParamsState(next),
      isUrlLoading: isEngagementFlowUrlSyncLoading,
      urlStateRevision: urlFilter,
    }),
    [isEngagementFlowUrlSyncLoading, setSearchParamsState, urlFilter],
  );

  const wasUrlSyncLoadingRef = useRef(isEngagementFlowUrlSyncLoading);

  useEffect(() => {
    const wasLoading = wasUrlSyncLoadingRef.current;
    wasUrlSyncLoadingRef.current = isEngagementFlowUrlSyncLoading;

    if (!wasLoading || isEngagementFlowUrlSyncLoading || !gridApiRef.current) {
      return;
    }

    const expectedFilterModel = mapEngagementFlowUrlToGridState(urlFilter).filterModel;
    if (Object.keys(expectedFilterModel).length === 0) {
      return;
    }

    const currentFilterModel = gridApiRef.current.getFilterModel() ?? {};
    if (!isEqual(currentFilterModel, expectedFilterModel)) {
      gridApiRef.current.setFilterModel(expectedFilterModel);
    }
  }, [isEngagementFlowUrlSyncLoading, urlFilter]);

  useEffect(() => {
    if (isGridLoading) {
      return;
    }
    const columns = isPendingEngagementsFilterByFindingsEnabled()
      ? ['findingKinds', 'tagFilters']
      : ['findingKinds'];
    gridApiRef.current?.refreshCells({ columns, force: true });
  }, [connectorProviderMap, findingSpecById, isGridLoading, rowData, tagSpecs]);

  const { connectorsByCategoryGroup } = useConnectorData();

  const tagSpecIdToName = useMemo(() => buildTagSpecIdToNameLookup(tagSpecs), [tagSpecs]);
  const tagSpecIdToNameRef = useRef(tagSpecIdToName);
  tagSpecIdToNameRef.current = tagSpecIdToName;

  const { data: fetchUserCohorts } = useApiObjectData<CohortDto[], OptionType[]>(
    searchText,
    getUserCohorts,
    (dataItem) =>
      dataItem &&
      dataItem.map((item) => ({
        id: item.value,
        displayValue: item.displayValue,
        value: item.value,
        label: item.displayValue,
        icon: connectorsByCategoryGroup?.['Identity & Access']?.[0]?.imageUrl,
      })),
  );

  const handleUpdateStatus = useCallback(
    async (id: string, value: FlowSpecStatusKind) => {
      try {
        // Fetch the full spec so fields absent from the _summary response
        // (e.g. engagementChannelCids, filter.cohorts, triggerFilter) are
        // preserved in the PUT body rather than wiped.
        const res = await getEngagementFlow('flowSpecs', { id });
        const fullSpec = res?.data?.[0] ?? rowData.find((item) => item.id === id) ?? ({} as FlowSpecDto);
        await updateEngagementFlow({ ...fullSpec, status: value });
        refresh();
      } catch {
        addToast({ severity: 'error', body: DEFAULT_ERROR_MSG });
      }
    },
    [rowData, refresh, addToast],
  );

  const handleMoreActions = useCallback(
    async (item: string, id: string) => {
      switch (item) {
        case 'DELETE':
          setShowDeleteModal(true);
          setSelectedId(id);
          break;
        case 'DUPLICATE': {
          const duplicateData = getEngagementFlow('flowSpecs', { id }).then(
            (res: Page<FlowSpecDto> | undefined) => {
              if (!res || !res.data.length) {
                return {} as FlowSpecDto;
              }
              return res.data[0];
            },
          );
          if (duplicateData) {
            onDuplicate(await duplicateData);
          }
          break;
        }
        case 'TEST': {
          setShowTestEngagementModal(true);
          const res = await getEngagementFlow('flowSpecs', { id });
          if (res && res.data.length) {
            setTestFlowSpec(res.data[0]);
          } else {
            setTestFlowSpec({} as FlowSpecDto);
          }
          break;
        }
        case 'ENGAGEMENT LOG':
          navigate(`${DEFAULT_WORKFLOW_ROUTE}/engagement-logs/${id}`);
          break;
        default:
          break;
      }
    },
    [onDuplicate, navigate],
  );

  const handleShowActiveEngagementsModal = useCallback(
    (flowSpecId: string, variant: EngagementModalVariant = 'all') => {
      setSelectedId(flowSpecId);
      setModalVariant(variant);
      setShowEngagementsModal(true);
    },
    [],
  );

  const handleDeleteClick = useCallback(async () => {
    try {
      const res = await getEngagementFlow('flowSpecs', { id: selectedId });
      const fullSpec = res?.data?.[0] ?? rowData.find((item) => item.id === selectedId) ?? ({} as FlowSpecDto);
      await updateEngagementFlow({ ...fullSpec, status: FlowSpecStatusKind.ARCHIVE });
      refresh();
    } catch {
      addToast({ severity: 'error', body: DEFAULT_ERROR_MSG });
    }
    setShowDeleteModal(false);
  }, [rowData, selectedId, refresh, addToast]);

  const handleCloseDeleteModal = useCallback(() => setShowDeleteModal(false), []);
  const handleCloseEngagementsModal = useCallback(() => setShowEngagementsModal(false), []);
  const handleCloseTestEngagementModal = useCallback(() => {
    setSelectedOptions([]);
    setShowTestEngagementModal(false);
  }, []);

  const handleTriggerButtonClick = useCallback(() => {
    testEngagement(
      testFlowSpec,
      selectedOptions.map((option) => option.value),
    )
      .then(() => {
        addToast({
          severity: 'success',
          body: getToastBody(ENGAGE_SUCCESS_MSG),
        });
      })
      .catch((error) => {
        addToast({
          severity: 'error',
          body: DEFAULT_ERROR_MSG + error.message,
        });
      });
    setSelectedOptions([]);
    setShowTestEngagementModal(false);
  }, [testFlowSpec, selectedOptions, addToast]);

  const handleOptionSelected = useCallback(
    (options: MultiValue<{ value: string; label: string }>) => {
      setSelectedOptions(options as OptionType[]);
    },
    [],
  );

  const handleSearchTextChange = useCallback((inputValue: string) => {
    setSearchText(inputValue);
  }, []);

  // Stable refs for callbacks used inside column defs to avoid recreating column defs on every render
  const handleUpdateStatusRef = useRef(handleUpdateStatus);
  handleUpdateStatusRef.current = handleUpdateStatus;

  const handleMoreActionsRef = useRef(handleMoreActions);
  handleMoreActionsRef.current = handleMoreActions;

  const handleShowActiveEngagementsModalRef = useRef(handleShowActiveEngagementsModal);
  handleShowActiveEngagementsModalRef.current = handleShowActiveEngagementsModal;

  const moreOptionsRef = useRef<EngagementMenuOption[]>(moreOptions);
  moreOptionsRef.current = moreOptions;

  const columnDefs: ColDef<FlowSpecDto>[] = useMemo(
    () => [
      {
        colId: 'name',
        field: 'name',
        headerName: FLOW_HEADER_TEXTS[0],
        sortable: true,
        flex: 3,
        minWidth: 250,
        cellRenderer: (params: ICellRendererParams<FlowSpecDto>) => {
          const row = params.data;
          if (!row) {
            return null;
          }
          return (
            <Link
              to={`${DEFAULT_WORKFLOW_ROUTE}/create-engagement/${row.id}`}
              className="text-decoration-none ag-engagement-flow__link text-truncate bodyFont d-block"
              title={row.name}
            >{row.name}</Link>
          );
        },
      },
      {
        colId: 'filter',
        headerName: FLOW_HEADER_TEXTS[1],
        sortable: false,
        flex: 2.5,
        minWidth: 180,
        valueGetter: (p) => p.data?.filter?.cohorts.map((item) => startCase(item?.displayValue)).join(', ') || 'ALL USERS',
        cellRenderer: TruncatedTextCellRenderer,
      },
      {
        colId: 'findingKinds',
        headerName: FLOW_SPEC_TARGET_FINDINGS,
        sortable: true,
        flex: 2.5,
        minWidth: 320,
        filter: 'agSetColumnFilter',
        filterParams: {
          valueFormatter: (params: { value: string }) =>
            formatDisplayLabelRef.current(String(params.value ?? '').toLowerCase()),
        },
        filterValueGetter: (p) => getFindingKindsFromFlowSpec(p.data ?? ({} as FlowSpecDto)),
        valueGetter: (p) =>
          formatTargetFindingsForSearch(p.data, {
            connectorProviderMap: connectorProviderMapRef.current,
            findingSpecById: findingSpecByIdRef.current,
            formatDisplayLabel: formatDisplayLabelRef.current,
          }) || '-',
        cellRenderer: TargetFindingsCellRenderer,
        cellRendererParams: {
          connectorProviderMapRef,
          findingSpecByIdRef,
          formatDisplayLabelRef,
        },
      },
      ...(isPendingEngagementsFilterByFindingsEnabled()
        ? [
            {
              colId: 'tagFilters',
              headerName: 'Filters',
              sortable: false,
              flex: 2,
              minWidth: 220,
              valueGetter: (p: { data?: FlowSpecDto }) => {
                const row = p.data;
                if (!row) {
                  return '-';
                }

                const buckets = parseTagBucketsFromTriggerFilter(row.triggerFilter);
                const formatted = formatTagFiltersPlainLanguage(buckets, tagSpecIdToNameRef.current);

                return formatted || '-';
              },
              cellRenderer: TruncatedTextCellRenderer,
            } satisfies ColDef<FlowSpecDto>,
          ]
        : []),
      {
        colId: 'lastTriggered',
        headerName: FLOW_HEADER_TEXTS[2],
        sortable: false,
        flex: 1.5,
        minWidth: 190,
        valueGetter: (p) => p.data?.activity?.lastTriggered ? DateTimeFormatter(p.data?.activity?.lastTriggered) : 'N/A',
        cellRenderer: TruncatedTextCellRenderer,
      },
      {
        colId: 'total',
        headerName: FLOW_HEADER_TEXTS[4],
        sortable: false,
        wrapHeaderText: true,
        autoHeaderHeight: true,
        width: 140,
        minWidth: 140,
        valueGetter: (p) => p.data?.activity?.totalCount ?? 0,
        cellRenderer: TruncatedTextCellRenderer,
      },
      {
        colId: 'active',
        headerName: FLOW_HEADER_TEXTS[3],
        sortable: false,
        wrapHeaderText: true,
        autoHeaderHeight: true,
        width: 140,
        minWidth: 140,
        valueGetter: (p) => p.data?.activity?.activeCount ?? 0,
        cellRenderer: TruncatedTextCellRenderer,
      },
      {
        colId: 'avgHealingTime',
        headerName: FLOW_HEADER_TEXTS[8],
        sortable: false,
        wrapHeaderText: true,
        autoHeaderHeight: true,
        width: 140,
        minWidth: 100,
        valueGetter: (p) => p.data?.activity?.avgHealingTime === 0 ? '-' : moment.duration(p.data?.activity?.avgHealingTime?.toString()).humanize(),
        cellRenderer: TruncatedTextCellRenderer,
      },
      {
        colId: 'status',
        field: 'status',
        headerName: FLOW_HEADER_TEXTS[5],
        pinned: 'right',
        lockPinned: true,
        sortable: true,
        width: 100,
        minWidth: 100,
        cellRenderer: (params: ICellRendererParams<FlowSpecDto>) => {
          const row = params.data;
          if (!row) {
            return null;
          }
          const menuItems = Object.values(FlowSpecStatusKind)
            .filter(
              (item) => item !== FlowSpecStatusKind.ARCHIVE && item !== FlowSpecStatusKind.ERROR,
            )
            .map((value) => ({ id: value, value }));
          return (
            <div className="select-menu-wrapper">
              <Dropdown>
                <Dropdown.Toggle className="border-0 text-text-low-emphasis d-flex gap-2 justify-content-center align-items-center">
                  <Chip value={row.status} variant="status" />
                  <RiArrowDownSLine />
                </Dropdown.Toggle>
                <Dropdown.Menu className="p-0 w-100 gap-2 bg-body-color border-text-medium-emphasis dropdown__custom-menu">
                  {menuItems.map((item) => (
                    <Dropdown.Item
                      key={item.id}
                      className="dropdown__menu-item d-flex align-items-center gap-2 bodyFont"
                      onClick={() => handleUpdateStatusRef.current(row.id, item.value)}
                    >{item.value}</Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            </div>
          );
        },
      },
      {
        colId: 'actions',
        headerName: '',
        sortable: false,
        width: 60,
        minWidth: 60,
        pinned: 'right',
        lockPinned: true,
        suppressHeaderMenuButton: true,
        cellRenderer: (params: ICellRendererParams<FlowSpecDto>) => {
          const row = params.data;
          if (!row) {
            return null;
          }
          return (
            <div className="select-menu-wrapper">
              <Dropdown>
                <Dropdown.Toggle className="border-0 text-text-low-emphasis d-flex">
                  <TbDotsVertical />
                </Dropdown.Toggle>
                <Dropdown.Menu className="p-0 gap-2 bg-body-color border-text-medium-emphasis dropdown__custom-menu">
                  {moreOptionsRef.current.map((item) => (
                    <Dropdown.Item
                      key={item.id}
                      className="dropdown__menu-item d-flex align-items-center gap-2 bodyFont"
                      onClick={() => handleMoreActionsRef.current(item.value, row.id)}
                    >{item.value}</Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            </div>
          );
        },
      },
    ],
    [],
  );

  const { applyOnGridReady, onColumnEvent } = useAgGridColumnStatePersistence({
    storageKey: COLUMN_STATE_STORAGE_KEYS.server,
    defaultState: getDefaultEngagementFlowColumnState(),
    columnDefs,
  });

  const handleGridReady = useCallback(
    (event: GridReadyEvent<FlowSpecDto>) => {
      gridApiRef.current = event.api;
      applyOnGridReady(
        event.api as unknown as Parameters<typeof applyOnGridReady>[0],
      );
    },
    [applyOnGridReady],
  );

  const handleColumnPersistenceEvent = useCallback(
    (
      event:
        | ColumnResizedEvent<FlowSpecDto>
        | ColumnMovedEvent<FlowSpecDto>
        | ColumnVisibleEvent<FlowSpecDto>
        | ColumnPinnedEvent<FlowSpecDto>,
    ) => {
      onColumnEvent(event as unknown as Parameters<typeof onColumnEvent>[0]);
    },
    [onColumnEvent],
  );

  const getRowHeight = useCallback((params: RowHeightParams<FlowSpecDto>) => {
    return getEngagementFlowRowHeightForFindingCount(
      getTargetFindingCount(params.data),
      ENGAGEMENT_FLOW_ROW_HEIGHT,
      ENGAGEMENT_FLOW_FINDING_LINE_HEIGHT,
    );
  }, []);

  return (
    <div className="ag-engagement-flow-grid h-100">
      <AgGridBase.Root>
        <AgGridBase.Header hidden />
        <AgGridBase.Table<FlowSpecDto>
          columnDefs={columnDefs}
          dataMode="client"
          rowData={rowData}
          showSidebar={false}
          wrapperClassName="ag-engagement-flow-grid__wrapper ag-engagement-flow-grid__wrapper--theme-findings ag-grid-theme--amp-dark"
          bodyHeight={tableBodyHeight}
          showSearchBar={true}
          onSearchChange={handleGridSearchChange}
          urlSync={engagementFlowUrlSync}
          agGridProps={{
            loading: isGridLoading,
            getRowId: (params) => String(params.data?.id ?? ''),
            suppressAggFuncInHeader: true,
            suppressContextMenu: true,
            animateRows: false,
            isExternalFilterPresent,
            doesExternalFilterPass,
            getRowHeight,
            onGridReady: handleGridReady,
            onGridPreDestroyed: () => {
              gridApiRef.current = null;
            },
            onColumnResized: handleColumnPersistenceEvent,
            onColumnMoved: handleColumnPersistenceEvent,
            onColumnVisible: handleColumnPersistenceEvent,
            onColumnPinned: handleColumnPersistenceEvent,
            noRowsOverlayComponent: EmptyState,
            noRowsOverlayComponentParams: {
              ImageSrc: EngageIcon,
              noDescription: false,
              description: 'Create an AI Agent to get started',
            },
          }}
        />
      </AgGridBase.Root>

      <ConfirmationModal
        modalTitle={t(DELETE_ENGAGEMENT)}
        modalBodyTitle={t(ENGAGEMENT_MODAL_CONTENT)}
        onPrimaryButtonClick={handleDeleteClick}
        primaryButtonLabel={YES_DELETE_IT}
        show={showDeleteModal}
        onClose={handleCloseDeleteModal}
        buttonVariant="danger"
      />
      <EngagementsModal
        variant={modalVariant}
        show={showEngagementsModal}
        onClose={handleCloseEngagementsModal}
        specId={selectedId}
      />
      <TestEngagementModal
        modalTitle={t(TEST_ENGAGEMENT)}
        onPrimaryButtonClick={handleTriggerButtonClick}
        primaryButtonLabel={TRIGGER_ENGAGEMENT}
        show={showTestEngagementModal}
        onClose={handleCloseTestEngagementModal}
        buttonVariant="primary"
        modalContent={
          <div className="test-engagement-modal__multi-select-container">
            <ChipMultiSelect
              id="id"
              selected={selectedOptions}
              onChange={handleOptionSelected}
              placeholder={
                <Typography variant="body3" className="engagement-flow__placeholder mb-0">
                  {SELECT_USERS_PLACEHOLDER_TEXT}
                </Typography>
              }
              options={fetchUserCohorts}
              onInputChange={handleSearchTextChange}
            />
          </div>
        }
        primaryButtonDisabled={selectedOptions.length === 0}
      />
    </div>
  );
};

export default AgGridEngagementFlow;
